import { FC, useState, useEffect, useCallback } from 'react';
import { useToast } from '../../../ui/Toast';
import {
  useTherapistProfile,
  useUpdateTherapistProfile,
} from '../../../../hooks/useTherapistDashboard';
import { ProfileHeader } from './ProfileHeader';
import { StatsDisplay } from './StatsDisplay';
import { PersonalInfoForm } from './PersonalInfoForm';
import { SpecialtiesSection } from './SpecialtiesSection';
import { CertificationsSection } from './CertificationsSection';
import { LanguagesSection } from './LanguagesSection';
import { SettingsPanel } from './SettingsPanel';
import { ProfilePageHeader } from './ProfilePageHeader';
import { ProfileLoadingState } from './ProfileLoadingState';
import { ProfileErrorState } from './ProfileErrorState';
import {
  EditableProfileData,
  extractEditableData,
  hasDataChanged,
} from './types';

export const ProfileSection: FC = () => {
  const { showToast } = useToast();
  const { data: profile, isLoading, error, refetch } = useTherapistProfile();
  const updateMutation = useUpdateTherapistProfile();

  const [formData, setFormData] = useState<EditableProfileData | null>(null);
  const [originalData, setOriginalData] = useState<EditableProfileData | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (profile) {
      const data = extractEditableData(profile);
      setFormData(data);
      setOriginalData(data);
      setHasChanges(false);
    }
  }, [profile]);

  useEffect(() => {
    if (formData && originalData) {
      setHasChanges(hasDataChanged(originalData, formData));
    }
  }, [formData, originalData]);

  const handleInputChange = useCallback(
    (field: keyof EditableProfileData, value: string | string[]) => {
      setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
    },
    []
  );

  const handleSettingsChange = useCallback(
    (field: keyof EditableProfileData['settings'], value: boolean | number) => {
      setFormData((prev) =>
        prev ? { ...prev, settings: { ...prev.settings, [field]: value } } : null
      );
    },
    []
  );

  const handleReset = useCallback(() => {
    if (originalData) {
      setFormData({ ...originalData, specialty: [...originalData.specialty] });
      setHasChanges(false);
    }
  }, [originalData]);

  const validateForm = useCallback((): string[] => {
    const errors: string[] = [];
    if (!formData) return errors;

    if (!formData.firstName.trim()) errors.push('First name is required');
    if (!formData.lastName.trim()) errors.push('Last name is required');
    if (formData.phone && !/^[+]?[\d\s()-]{10,}$/.test(formData.phone)) {
      errors.push('Please enter a valid phone number');
    }
    if (formData.bio && formData.bio.length > 1000) {
      errors.push('Bio must be less than 1000 characters');
    }
    if (formData.specialty.length === 0) {
      errors.push('At least one specialty is required');
    }
    if (formData.settings.maxDailyAppointments < 1) {
      errors.push('Max daily appointments must be at least 1');
    }
    if (formData.settings.bufferTimeBetweenAppointments < 0) {
      errors.push('Buffer time cannot be negative');
    }

    return errors;
  }, [formData]);

  const handleSave = useCallback(async () => {
    if (!formData) return;

    const errors = validateForm();
    if (errors.length > 0) {
      showToast({
        type: 'error',
        title: 'Validation Error',
        message: errors[0] ?? 'Please fix the errors before saving.',
      });
      return;
    }

    updateMutation.mutate(formData, {
      onSuccess: () => {
        showToast({
          type: 'success',
          title: 'Profile Updated',
          message: 'Your profile has been saved successfully.',
        });
        setHasChanges(false);
        refetch();
      },
      onError: (err) => {
        showToast({
          type: 'error',
          title: 'Update Failed',
          message: err.message || 'Failed to save profile. Please try again.',
        });
      },
    });
  }, [formData, validateForm, updateMutation, showToast, refetch]);

  if (isLoading) {
    return <ProfileLoadingState />;
  }

  if (error) {
    return <ProfileErrorState errorMessage={error.message} onRetry={() => refetch()} />;
  }

  if (!profile || !formData) {
    return null;
  }

  return (
    <div className="space-y-6">
      <ProfilePageHeader
        hasChanges={hasChanges}
        isSaving={updateMutation.isPending}
        onReset={handleReset}
        onSave={handleSave}
      />
      <ProfileHeader profile={profile} />
      <StatsDisplay profile={profile} />
      <PersonalInfoForm formData={formData} onInputChange={handleInputChange} />
      <SpecialtiesSection
        specialties={formData.specialty}
        onChange={(specialties) => handleInputChange('specialty', specialties)}
      />
      <CertificationsSection certifications={profile.certifications} />
      <LanguagesSection
        languages={profile.languages}
        yearsOfExperience={profile.yearsOfExperience}
      />
      <SettingsPanel
        settings={formData.settings}
        onSettingsChange={handleSettingsChange}
      />
    </div>
  );
};
